import { useState, type FormEvent } from 'react';
import { useLocalization } from '@/hooks/useLocalization';
import {
  validatePasswords,
  CREATE_PASSWORD_MESSAGE,
  PASSWORD_TOOLTIP_SPECIAL_CHARACTERS,
} from '@/utils/formValidators';
import { useResetPassword } from './hooks/useResetPassword';
import { SupportWarningDialog } from './components/SupportWarningDialog';
import {
  AuthInfoTooltip,
  AuthTextField,
  PasswordToggle,
  ProgressHud,
  authControlStyles as controls,
} from './components/AuthControls';
import { AppHeader } from '@/components/layout/AppHeader';
import { AppFooter } from '@/components/layout/AppFooter';
import styles from './ResetPasswordPage.module.css';

/**
 * ResetPasswordPage — handles the Firebase password reset link.
 *
 * Mirrors Flutter ResetPassword + ResetPasswordConsumer:
 *   1. User opens the reset link from the email with ?oobCode=&tenantId=
 *   2. The form shows straight away while the code is validated (spinner over it)
 *   3. On submit (FormValidators.validatePasswords): confirmPasswordReset via Firebase
 *   4. Expired link, success or failure: toast → /home (see useResetPassword)
 *
 * Flutter source files:
 *   - presentation/web/auth/reset_password/reset_password.dart
 *   - application/web_auth/web_login/web_login_bloc.dart
 *   - domain/core/constants/string_constants.dart (WebResetPwd, WebSignUpPageConstants)
 */

export function ResetPasswordPage() {
  const { t } = useLocalization();

  const { isLoading, warningMessage, handleResetPassword, clearWarning } = useResetPassword();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [confirmError, setConfirmError] = useState('');

  function handleSubmit(e: FormEvent) {
    e.preventDefault();

    const pwErr = validatePasswords({ pass: password }, t);
    const cpErr = validatePasswords({ pass: password, confirmPass: confirmPassword, isConfirm: true }, t);
    setPasswordError(pwErr);
    setConfirmError(cpErr);

    if (pwErr || cpErr) return;

    handleResetPassword(password);
  }

  return (
    <div className={styles.page}>
      <div className={styles.screen}>
        <AppHeader />

        <div className={styles.centerContent}>
          <div className={styles.formCard}>
            <form className={styles.form} onSubmit={handleSubmit} noValidate>
              <AuthTextField
                id="reset-password"
                label={t('Reset Password')}
                required
                labelAddon={<AuthInfoTooltip message={t(CREATE_PASSWORD_MESSAGE) + PASSWORD_TOOLTIP_SPECIAL_CHARACTERS} />}
                type={showPassword ? 'text' : 'password'}
                placeholder={t('Enter your Password')}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (passwordError) setPasswordError('');
                }}
                autoComplete="new-password"
                endAdornment={<PasswordToggle shown={showPassword} onToggle={() => setShowPassword(!showPassword)} />}
                error={passwordError}
                errorId="reset-password-error"
              />

              <AuthTextField
                id="reset-password-confirm"
                label={t('Re-type your password')}
                required
                type={showConfirmPassword ? 'text' : 'password'}
                placeholder={t('Re-type your password')}
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  if (confirmError) setConfirmError('');
                }}
                autoComplete="new-password"
                endAdornment={
                  <PasswordToggle shown={showConfirmPassword} onToggle={() => setShowConfirmPassword(!showConfirmPassword)} />
                }
                error={confirmError}
                errorId="reset-password-confirm-error"
              />

              {/* CustomButton(backgroundColor: primaryColorDark) */}
              <button
                type="submit"
                className={`${controls.customButton} ${controls.customButtonAccent} ${styles.submitBtn}`}
                disabled={isLoading}
              >
                {t('Continue')}
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* The footer is hidden while loading, as in Flutter */}
      {!isLoading && <AppFooter />}

      {/* Flutter ModalProgressHUD */}
      {isLoading && <ProgressHud />}

      {warningMessage && (
        <SupportWarningDialog message={warningMessage} onOk={clearWarning} />
      )}
    </div>
  );
}
